import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/Button';
import { CheckCircleIcon, FileTextIcon, DownloadIcon, PrinterIcon, ShareIcon, ClockIcon, CreditCardIcon, BuildingIcon, CalendarIcon, ArrowRightIcon, ChevronRightIcon, HomeIcon, FileIcon } from 'lucide-react';
const ConfirmationPage: React.FC = () => {
  const {
    transactionId
  } = useParams<{
    transactionId: string;
  }>();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [showShareOptions, setShowShareOptions] = useState(false);
  // Application details from location state or default values
  const applicationDetails = {
    serviceId: location.state?.serviceId || 'SRV001',
    serviceName: location.state?.serviceName || 'Business Registration',
    amount: location.state?.amount || 15000,
    reference: location.state?.reference || transactionId || 'REF12345',
    date: location.state?.date || new Date().toLocaleDateString(),
    mda: location.state?.mda || 'Ministry of Commerce and Industry',
    estimatedCompletionDate: location.state?.estimatedCompletionDate || new Date(new Date().setDate(new Date().getDate() + 5)).toLocaleDateString(),
    status: 'Submitted'
  };
  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  const handlePrint = () => {
    window.print();
  };
  const handleDownload = () => {
    // In a real app, this would generate and download a PDF
    alert('Receipt downloaded successfully');
  };
  const toggleShareOptions = () => {
    setShowShareOptions(!showShareOptions);
  };
  if (isLoading) {
    return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-4965">
        <Header data-id="element-4966" />
        <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-id="element-4967">
          <div className="w-full max-w-md text-center" data-id="element-4968">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" data-id="element-4969"></div>
            <p className="text-gray-600" data-id="element-4970">Loading your application details...</p>
          </div>
        </main>
        <Footer data-id="element-4971" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-4972">
      <Header data-id="element-4973" />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8" data-id="element-4974">
        <div className="max-w-3xl mx-auto" data-id="element-4975">
          {/* Success Banner */}
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-md" data-id="element-4976">
            <div className="flex" data-id="element-4977">
              <div className="flex-shrink-0" data-id="element-4978">
                <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-4979" />
              </div>
              <div className="ml-3" data-id="element-4980">
                <h3 className="text-lg font-medium text-green-800" data-id="element-4981">
                  Application Submitted Successfully
                </h3>
                <p className="text-green-700 mt-1" data-id="element-4982">
                  Your application has been received and is being processed. You
                  can track its status using the reference number.
                </p>
              </div>
            </div>
          </div>
          {/* Application Receipt Card */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6 print:shadow-none" data-id="element-4983">
            {/* Header */}
            <div className="bg-green-600 px-6 py-4 print:bg-green-600" data-id="element-4984">
              <div className="flex justify-between items-center" data-id="element-4985">
                <h2 className="text-xl font-bold text-white" data-id="element-4986">
                  Application Receipt
                </h2>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4987">
                  {applicationDetails.status}
                </span>
              </div>
            </div>
            {/* Receipt Content */}
            <div className="p-6" data-id="element-4988">
              {/* Reference and Date */}
              <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center" data-id="element-4989">
                <div data-id="element-4990">
                  <p className="text-sm text-gray-500" data-id="element-4991">Reference Number</p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-4992">
                    {applicationDetails.reference}
                  </p>
                </div>
                <div className="mt-2 sm:mt-0 flex items-center" data-id="element-4993">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-4994" />
                  <span className="text-sm text-gray-500" data-id="element-4995">
                    Submitted on {applicationDetails.date}
                  </span>
                </div>
              </div>
              {/* Service Details */}
              <div className="border-t border-b border-gray-200 py-4 mb-4" data-id="element-4996">
                <h3 className="text-base font-medium text-gray-900 mb-3" data-id="element-4997">
                  Service Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4998">
                  <div data-id="element-4999">
                    <p className="text-sm text-gray-500" data-id="element-5000">Service Name</p>
                    <p className="text-base font-medium text-gray-900" data-id="element-5001">
                      {applicationDetails.serviceName}
                    </p>
                  </div>
                  <div data-id="element-5002">
                    <p className="text-sm text-gray-500" data-id="element-5003">Providing MDA</p>
                    <p className="text-base font-medium text-gray-900" data-id="element-5004">
                      {applicationDetails.mda}
                    </p>
                  </div>
                  <div data-id="element-5005">
                    <p className="text-sm text-gray-500" data-id="element-5006">Service ID</p>
                    <p className="text-base font-medium text-gray-900" data-id="element-5007">
                      {applicationDetails.serviceId}
                    </p>
                  </div>
                  <div data-id="element-5008">
                    <p className="text-sm text-gray-500" data-id="element-5009">Fee Paid</p>
                    <p className="text-base font-medium text-gray-900" data-id="element-5010">
                      ₦{applicationDetails.amount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              {/* Status and Timeline */}
              <div className="mb-6" data-id="element-5011">
                <h3 className="text-base font-medium text-gray-900 mb-3" data-id="element-5012">
                  Processing Timeline
                </h3>
                <div className="relative" data-id="element-5013">
                  {/* Timeline Line */}
                  <div className="absolute h-full w-0.5 bg-gray-200 left-1.5 top-0" data-id="element-5014"></div>
                  {/* Steps */}
                  <div className="relative pb-6" data-id="element-5015">
                    <div className="flex items-start" data-id="element-5016">
                      <div className="h-4 w-4 rounded-full bg-green-500 mt-1 mr-3 flex-shrink-0" data-id="element-5017"></div>
                      <div data-id="element-5018">
                        <p className="text-sm font-medium text-gray-900" data-id="element-5019">
                          Application Submitted
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-5020">
                          {applicationDetails.date}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="relative pb-6" data-id="element-5021">
                    <div className="flex items-start" data-id="element-5022">
                      <div className="h-4 w-4 rounded-full bg-gray-300 mt-1 mr-3 flex-shrink-0" data-id="element-5023"></div>
                      <div data-id="element-5024">
                        <p className="text-sm font-medium text-gray-700" data-id="element-5025">
                          Under Review
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-5026">
                          Your application is being reviewed
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="relative pb-6" data-id="element-5027">
                    <div className="flex items-start" data-id="element-5028">
                      <div className="h-4 w-4 rounded-full bg-gray-300 mt-1 mr-3 flex-shrink-0" data-id="element-5029"></div>
                      <div data-id="element-5030">
                        <p className="text-sm font-medium text-gray-700" data-id="element-5031">
                          Processing
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-5032">
                          Your application is being processed
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="relative" data-id="element-5033">
                    <div className="flex items-start" data-id="element-5034">
                      <div className="h-4 w-4 rounded-full bg-gray-300 mt-1 mr-3 flex-shrink-0" data-id="element-5035"></div>
                      <div data-id="element-5036">
                        <p className="text-sm font-medium text-gray-700" data-id="element-5037">
                          Completed
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-5038">
                          Estimated completion:{' '}
                          {applicationDetails.estimatedCompletionDate}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* What's Next */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6" data-id="element-5039">
                <h3 className="text-sm font-medium text-blue-800 mb-2" data-id="element-5040">
                  What's Next?
                </h3>
                <ul className="text-sm text-blue-700 space-y-2" data-id="element-5041">
                  <li className="flex items-start" data-id="element-5042">
                    <ChevronRightIcon className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" data-id="element-5043" />
                    <span data-id="element-5044">
                      You will receive notifications as your application
                      progresses
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5045">
                    <ChevronRightIcon className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" data-id="element-5046" />
                    <span data-id="element-5047">
                      You can track your application status using the reference
                      number
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5048">
                    <ChevronRightIcon className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" data-id="element-5049" />
                    <span data-id="element-5050">
                      You may be contacted if additional information is required
                    </span>
                  </li>
                </ul>
              </div>
              {/* Actions */}
              <div className="flex flex-wrap gap-3 justify-center print:hidden" data-id="element-5051">
                <Button variant="outline" onClick={handlePrint} className="flex items-center" data-id="element-5052">
                  <PrinterIcon className="h-4 w-4 mr-2" data-id="element-5053" />
                  Print Receipt
                </Button>
                <Button variant="outline" onClick={handleDownload} className="flex items-center" data-id="element-5054">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-5055" />
                  Download Receipt
                </Button>
                <div className="relative" data-id="element-5056">
                  <Button variant="outline" onClick={toggleShareOptions} className="flex items-center" data-id="element-5057">
                    <ShareIcon className="h-4 w-4 mr-2" data-id="element-5058" />
                    Share Receipt
                  </Button>
                  {showShareOptions && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10" data-id="element-5059">
                      <div className="py-1" data-id="element-5060">
                        <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-5061">
                          Share via Email
                        </button>
                        <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-5062">
                          Share via WhatsApp
                        </button>
                        <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-5063">
                          Copy Link
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>
          {/* Next Steps Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 print:hidden" data-id="element-5064">
            <Link to="/my-applications" className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-5065">
              <div className="flex items-center mb-2" data-id="element-5066">
                <div className="p-2 bg-blue-100 rounded-md mr-3" data-id="element-5067">
                  <FileTextIcon className="h-5 w-5 text-blue-600" data-id="element-5068" />
                </div>
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5069">
                  My Applications
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3" data-id="element-5070">
                View all your applications and their current status
              </p>
              <div className="flex items-center text-blue-600 text-sm font-medium" data-id="element-5071">
                View Applications
                <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-5072" />
              </div>
            </Link>
            <Link to="/track-application" className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-5073">
              <div className="flex items-center mb-2" data-id="element-5074">
                <div className="p-2 bg-green-100 rounded-md mr-3" data-id="element-5075">
                  <ClockIcon className="h-5 w-5 text-green-600" data-id="element-5076" />
                </div>
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5077">
                  Track Application
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3" data-id="element-5078">
                Check the detailed status of this or any other application
              </p>
              <div className="flex items-center text-green-600 text-sm font-medium" data-id="element-5079">
                Track Application
                <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-5080" />
              </div>
            </Link>
          </div>
          {/* Return to Dashboard */}
          <div className="text-center print:hidden" data-id="element-5081">
            <Link to="/dashboard" data-id="element-5082">
              <Button variant="primary" className="flex items-center mx-auto" data-id="element-5083">
                <HomeIcon className="h-4 w-4 mr-2" data-id="element-5084" />
                Return to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer data-id="element-5085" />
    </div>;
};
export default ConfirmationPage;